import { Fragment, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { CourseAdminCard } from '@/components/shared/CourseAdminCard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Badge } from '@/components/ui/daisy';
import { WithClassname } from '@/components/ui/types';
import useCourseTreeNavigate from '@/data/hook/course-line/useCourseTreeNavigate';
import CoursePath from '@/models/CoursePath';
import { Breakpoints } from '@/utils/Breakpoint';
import { mountKey } from '@/utils/courses';
import { ROUTES } from '@/utils/routes';
import BadgeFormats from './BadgeFormats';
import ContPedStatus from './PedContentStatus';

type PedContentCardProps = {
  coursePath: CoursePath;
  courseLineId: number;
  moduleId: number;
  actions?: ReactNode;
  onDelete?: (slug: string) => void;
  isDeleting?: boolean;
} & WithClassname;

export default function PedContentCard({
  coursePath,
  courseLineId,
  moduleId,
  actions,
  onDelete,
  isDeleting,
  className,
}: PedContentCardProps) {
  const { navigate } = useCourseTreeNavigate();
  const { t } = useTranslation('translation');
  const isXl = useMediaQuery({ minWidth: Breakpoints.xl });

  const handleEdit = () => {
    navigate(
      ROUTES.ADMIN.COURSE_LINES.PED_CONTENT(
        courseLineId,
        moduleId,
        coursePath.slug,
      ),
      { treeKey: mountKey({ type: 'course', id: coursePath.slug }) },
    );
  };

  return (
    <CourseAdminCard isDeleting={isDeleting} className={className}>
      <CourseAdminCard.Header data-testid="course-path-card">
        <CourseAdminCard.Leading className="flex-col items-start">
          <div className="flex gap-4 items-center">
            <CourseAdminCard.Title>
              {coursePath.course.name}
            </CourseAdminCard.Title>
            <span
              data-testid="course-path-abbreviation"
              className="text-base-content max-xl:text-xs text-sm shrink-0"
            >
              {coursePath.course.abbreviation}
            </span>
            <Badge variant="soft" size={isXl ? 'sm' : 'xs'}>
              {`v ${coursePath.version.toFixed(1)}`}
            </Badge>
            <ContPedStatus
              status={coursePath.status}
              version={coursePath.version}
              size={isXl ? 'sm' : 'xs'}
            />
          </div>
          <ConditionalRenderer
            condition={coursePath.course.businessModels?.length}
          >
            <CourseAdminCard.Badges className="max-xl:gap-2 gap-4">
              <BadgeFormats
                size={isXl ? 'md' : 'sm'}
                formats={coursePath.course.formats}
              />
              {coursePath.course.businessModels?.map(model => (
                <Badge key={model} variant="soft" size={isXl ? 'md' : 'sm'}>
                  {t(`businessModels.${model}`)}
                </Badge>
              ))}
            </CourseAdminCard.Badges>
          </ConditionalRenderer>
        </CourseAdminCard.Leading>
        <CourseAdminCard.Actions>
          {actions ?? (
            <Fragment>
              <CourseAdminCard.EditButton onClick={handleEdit} />
              <CourseAdminCard.MoreActions>
                {onDelete && (
                  <CourseAdminCard.MoreActions.Delete
                    onClick={() => onDelete(coursePath.slug)}
                  />
                )}
              </CourseAdminCard.MoreActions>
            </Fragment>
          )}
        </CourseAdminCard.Actions>
      </CourseAdminCard.Header>
    </CourseAdminCard>
  );
}
